/**
 * Contrôles des fichiers exportés par l'Arduino IDE avant un flash USB d'un datalogger ESP32 (sans DOM, testable hors navigateur).
 * Voir docker-stack/plans/flash-usb-navigateur-datalogger.md.
 */

export interface NamedFile {
  name: string;
}

export interface IdentifiedFiles<T extends NamedFile> {
  bootloader?: T;
  partitions?: T;
  app?: T;
  ignored: { file: T; reason: string }[];
  errors: string[];
}

export interface PartitionEntry {
  label: string;
  type: number;
  subtype: number;
  offset: number;
  size: number;
}

export interface FirmwareImages {
  bootloader: Uint8Array;
  partitions: Uint8Array;
  app: Uint8Array;
}

export interface ImagesCheck {
  errors: string[];
  /** Problèmes graves que l'utilisateur peut décider d'ignorer explicitement */
  blocking: string[];
  warnings: string[];
  info: { partitions: PartitionEntry[]; otaSize: number | null; appUsage: number | null; version: string | null };
}

/** Adresses de l'« ESP32 Dev Module » (platform.txt du core ESP32 3.3.12) */
export const ADDRESSES = {
  bootloader: 0x1000,
  partitions: 0x8000,
  bootApp0: 0xe000,
  app: 0x10000,
};

/** Emplacement OTA du schéma « Minimal SPIFFS (1.9MB APP with OTA) » */
export const MIN_SPIFFS_APP_SIZE = 0x1e0000;
/** Adresse de la NVS (identifiants Wi-Fi) dans les schémas Arduino 4 Mo */
export const NVS_OFFSET = 0x9000;

const ESP_IMAGE_MAGIC = 0xe9;

/** Classe les fichiers d'un export Arduino (« Croquis → Exporter les binaires compilés »). */
export function identifyFiles<T extends NamedFile>(files: T[]): IdentifiedFiles<T> {
  const result: IdentifiedFiles<T> = { ignored: [], errors: [] };
  const apps: T[] = [];

  for (const file of files) {
    const name = file.name;
    if (name.endsWith(".merged.bin")) {
      result.ignored.push({ file, reason: "image fusionnée : elle effacerait les identifiants Wi-Fi (NVS)" });
    } else if (name.endsWith(".bootloader.bin")) {
      result.bootloader = file;
    } else if (name.endsWith(".partitions.bin")) {
      result.partitions = file;
    } else if (name === "boot_app0.bin") {
      result.ignored.push({ file, reason: "fourni par la page" });
    } else if (name.endsWith(".ino.bin")) {
      apps.push(file);
    } else {
      result.ignored.push({ file, reason: "non utilisé" });
    }
  }

  if (apps.length > 1) {
    result.errors.push(`Plusieurs applications dans la sélection (${apps.map((f) => f.name).join(", ")}) : n'en garder qu'une.`);
  }
  result.app = apps[0];
  if (!result.bootloader) result.errors.push("Fichier « .ino.bootloader.bin » manquant.");
  if (!result.partitions) result.errors.push("Fichier « .ino.partitions.bin » manquant.");
  if (!result.app) result.errors.push("Fichier « .ino.bin » (application) manquant.");
  return result;
}

/** Lit une table de partitions ESP32 (entrées de 32 octets, magie 0xAA 0x50). */
export function parsePartitionTable(data: Uint8Array): PartitionEntry[] {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const entries: PartitionEntry[] = [];
  for (let pos = 0; pos + 32 <= data.length; pos += 32) {
    if (data[pos] !== 0xaa || data[pos + 1] !== 0x50) {
      break; // fin de table (0xFF) ou somme MD5 (0xEB 0xEB)
    }
    const labelBytes = data.slice(pos + 12, pos + 28);
    const end = labelBytes.indexOf(0);
    entries.push({
      type: data[pos + 2],
      subtype: data[pos + 3],
      offset: view.getUint32(pos + 4, true),
      size: view.getUint32(pos + 8, true),
      label: String.fromCharCode(...Array.from(end >= 0 ? labelBytes.slice(0, end) : labelBytes)),
    });
  }
  return entries;
}

/** Version « Firmware AAAA-MM-JJ… » écrite au démarrage par les sketchs depuis 2026-10-16. */
export function extractFirmwareVersion(app: Uint8Array): string | null {
  // windows-1252 : un octet = un caractère, suffisant pour chercher une chaîne ASCII
  const text = new TextDecoder("windows-1252").decode(app);
  const match = /Firmware (\d{4}-\d{2}-\d{2}[A-Za-z0-9._-]*)\0/.exec(text);
  return match ? match[1] : null;
}

/** Contrôles avant flash. */
export function checkImages(images: FirmwareImages): ImagesCheck {
  const errors: string[] = [];
  const blocking: string[] = [];
  const warnings: string[] = [];

  if (images.bootloader[0] !== ESP_IMAGE_MAGIC) errors.push("Le bootloader n'est pas une image ESP32.");
  if (images.app[0] !== ESP_IMAGE_MAGIC) errors.push("L'application n'est pas une image ESP32.");

  const partitions = parsePartitionTable(images.partitions);
  let otaSize: number | null = null;
  if (partitions.length === 0) {
    errors.push("Table de partitions illisible.");
  } else {
    const nvs = partitions.find((p) => p.type === 1 && p.subtype === 0x02);
    const ota0 = partitions.find((p) => p.type === 0 && p.subtype === 0x10);
    const ota1 = partitions.find((p) => p.type === 0 && p.subtype === 0x11);

    if (!nvs || nvs.offset !== NVS_OFFSET) {
      blocking.push("La NVS n'est pas à l'adresse habituelle (0x9000) : les identifiants Wi-Fi enregistrés seraient perdus.");
    }
    if (!ota0 || !ota1) {
      blocking.push("Pas d'emplacements OTA (app0 / app1) : la mise à jour à distance sera impossible.");
    } else {
      otaSize = ota0.size;
      if (ota0.size !== MIN_SPIFFS_APP_SIZE) {
        blocking.push(
          `Schéma de partition différent de « Minimal SPIFFS » (emplacement de ${(ota0.size / 1024 / 1024).toFixed(2).replace(".", ",")} Mo au lieu de 1,88 Mo) : `
          + "à recompiler avec Outils → Partition Scheme → « Minimal SPIFFS (1.9MB APP with OTA/190KB SPIFFS) ».",
        );
      }
      if (images.app.length > ota0.size) {
        errors.push(`L'application (${images.app.length} octets) ne tient pas dans l'emplacement (${ota0.size} octets).`);
      }
      if (images.app.length > ota0.size * 0.9) {
        warnings.push("L'application occupe plus de 90 % de l'emplacement : les prochaines versions risquent de ne plus passer en OTA.");
      }
    }
  }

  const version = extractFirmwareVersion(images.app);
  if (!version) {
    warnings.push("Version du firmware introuvable dans le binaire (sketch antérieur à 2026-10-16 ?).");
  }

  return {
    errors,
    blocking,
    warnings,
    info: {
      partitions,
      otaSize,
      appUsage: otaSize ? images.app.length / otaSize : null,
      version,
    },
  };
}

/**
 * MD5 (hexadécimal minuscule), pour la vérification par le boîtier après écriture.
 * Web Crypto ne propose pas MD5.
 */
export function md5Hex(data: Uint8Array): string {
  const s = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) {
    K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000);
  }

  const length = data.length;
  const paddedLength = (((length + 8) >>> 6) + 1) << 6;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(data);
  buffer[length] = 0x80;
  const view = new DataView(buffer.buffer);
  view.setUint32(paddedLength - 8, (length * 8) >>> 0, true);
  view.setUint32(paddedLength - 4, Math.floor(length / 0x20000000), true);

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;
  const M = new Uint32Array(16);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) {
      M[i] = view.getUint32(offset + i * 4, true);
    }
    let A = a0;
    let B = b0;
    let C = c0;
    let D = d0;
    for (let i = 0; i < 64; i++) {
      let F: number;
      let g: number;
      if (i < 16) {
        F = (B & C) | (~B & D);
        g = i;
      } else if (i < 32) {
        F = (D & B) | (~D & C);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        F = B ^ C ^ D;
        g = (3 * i + 5) % 16;
      } else {
        F = C ^ (B | ~D);
        g = (7 * i) % 16;
      }
      F = (F + A + K[i] + M[g]) >>> 0;
      A = D;
      D = C;
      C = B;
      B = (B + ((F << s[i]) | (F >>> (32 - s[i])))) >>> 0;
    }
    a0 = (a0 + A) >>> 0;
    b0 = (b0 + B) >>> 0;
    c0 = (c0 + C) >>> 0;
    d0 = (d0 + D) >>> 0;
  }

  const out = new DataView(new ArrayBuffer(16));
  out.setUint32(0, a0, true);
  out.setUint32(4, b0, true);
  out.setUint32(8, c0, true);
  out.setUint32(12, d0, true);
  let hex = "";
  for (let i = 0; i < 16; i++) {
    hex += out.getUint8(i).toString(16).padStart(2, "0");
  }
  return hex;
}
